/**
 * AdminLayoutRoute — custom oddits admin shell (sidebar, no topbar).
 * @file src/web/features/admin/components/AdminLayoutRoute.tsx
 *
 * A bespoke sidebar layout (no shared Header/topbar). Mounted by the
 * Router as a layout route, so the sidebar stays painted across
 * navigations — only the <Outlet /> content swaps.
 *
 * sidebar (fixed, oddits-branded) │ content area (<Outlet/>)
 *
 * On < lg the sidebar becomes a slide-over drawer toggled by a small
 * floating button (still no full-width topbar).
 *
 * Pages render their own <AdminPageHeader> (title + breadcrumbs) inside
 * the content — the shell owns no page-level meta.
 */

import { Suspense, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  NotebookPen,
  Landmark,
  Settings,
  LogOut,
  Menu,
  X,
  Upload,
  BookOpenText,
  ClipboardCheck,
  CalendarRange,
  Building2,
  ChevronDown,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { useAuth } from '../../auth/hooks';
import { isEntityUser, roleOf } from '../../../shared/utils/roles';
import { CompanyProvider, useCompany } from '../lib/company-context';
import { PeriodProvider, usePeriod } from '../lib/period-context';
import { brand } from '../../../shared/brand';

// CA app (dashboard, ledgers, day book, import). Professional + client tiers, plus
// platform admin for support. AuthGuard does EXACT match, so list each.
// (Data is tenant-scoped server-side once Phase 2 lands.)
const DEFAULT_ADMIN_ROLES = [
  'admin.tenant', // Professional Admin
  'moderator.manage', // Professional Moderator (and, incidentally, Platform Moderator)
  'moderator.approve', // Client Admin (interim)
  'moderator.review', // Client Moderator (interim)
  'admin.system', // Platform Admin (support / impersonation)
];

interface NavEntry {
  label: string;
  href: string;
  icon: LucideIcon;
  /** Professional-admin only — hidden from entity (client) logins. */
  professionalOnly?: boolean;
}

/** Which stakeholder this account is — colours the badge under the logo. */
function contextTag(roleLevel: string): { label: string; cls: string; dot: string } {
  if (roleLevel === 'admin.system') return { label: 'Platform', cls: 'bg-inkblack text-highlighter', dot: 'bg-highlighter' };
  if (roleLevel === 'moderator.approve' || roleLevel === 'moderator.review') return { label: 'Entity', cls: 'bg-warn-50 text-warn-600', dot: 'bg-warn-500' };
  return { label: 'Professional', cls: 'bg-ok-50 text-ok-600', dot: 'bg-ok-500' };
}

const NAV: NavEntry[] = [
  { label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { label: 'Entities', href: '/admin/entities', icon: Building2, professionalOnly: true },
  { label: 'Masters', href: '/admin/masters', icon: ClipboardCheck },
  { label: 'Books', href: '/admin/books', icon: NotebookPen },
  { label: 'GST', href: '/admin/gst', icon: Landmark },
  { label: 'Reports', href: '/admin/reports', icon: BookOpenText },
  { label: 'Import', href: '/admin/import', icon: Upload },
  // Settings lives in the account block at the foot of the sidebar — it is
  // account-level, not a place you work. Team + Activity are tabs inside it.
];

/** Shared look for the two header scope pickers. */
const scopeSelectCls =
  'cursor-pointer appearance-none truncate rounded-lg border border-receipt bg-paper py-1.5 pl-8 pr-7 text-sm font-medium text-inkblack transition-colors hover:border-inkblack-40 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter disabled:cursor-default';

/**
 * Entity names are long ("TECHINTELLI SOLUTIONS PRIVATE LIMITED") and the
 * financial year is always short, so they cannot share a width.
 *
 * On mobile the entity is pinned to the same narrow width as the year, because
 * the header also carries the hamburger and the logo and the name would
 * otherwise crowd them out — truncated there is the right trade. From sm up it
 * sizes to its content so the full name reads, capped so one very long client
 * cannot push the year off the edge.
 */
const entitySelectCls = `${scopeSelectCls} w-[9.5rem] sm:w-auto sm:max-w-[24rem]`;
const fySelectCls = `${scopeSelectCls} w-[9.5rem]`;

/**
 * Which client is in scope. Sits in the workspace header rather than the
 * sidebar: it scopes the CONTENT, so it belongs above the content and stays put
 * when the sidebar collapses.
 */
function CompanySwitcher() {
  const { entities, entityId, setEntityId, loading } = useCompany();
  if (loading || entities.length === 0) return null;
  const single = entities.length === 1;

  return (
    <div className="relative" title="Entity">
      <Building2 className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
      <select
        value={entityId ?? ''}
        onChange={(e) => setEntityId(e.target.value || null)}
        disabled={single}
        aria-label="Entity"
        className={entitySelectCls}
      >
        {!single && <option value="">All entities</option>}
        {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
    </div>
  );
}

/**
 * Financial-year picker, reading the same period context the report pages use,
 * so choosing a year moves every register, report and return at once.
 *
 * Hidden until the books have data — an empty year list is not a choice.
 */
function FySwitcher() {
  const p = usePeriod();
  if (!p.ready || p.fyOptions.length === 0) return null;

  return (
    <div className="relative" title="Financial year">
      <CalendarRange className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
      <select
        value={p.startYear ?? ''}
        onChange={(e) => p.setStartYear(Number(e.target.value))}
        aria-label="Financial year"
        className={fySelectCls}
      >
        {p.fyOptions.map((o) => (
          <option key={o.startYear} value={o.startYear}>FY {o.label}</option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-inkblack-60" />
    </div>
  );
}

function AdminContentFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" aria-label="Loading page">
      <div className="size-6 animate-spin rounded-full border-2 border-receipt border-t-brand-600" />
    </div>
  );
}

/**
 * Hover label for an icon that has no text beside it.
 *
 * The native `title` attribute technically works but waits about a second and
 * cannot be styled, which is too slow to feel like a label — you have already
 * guessed and clicked. This shows immediately and reads as part of the UI.
 *
 * Placement follows the context. Collapsed, the sidebar is a narrow rail and the
 * label belongs OUTSIDE it, to the right, where there is room to read it.
 * Expanded, the account icons sit at the foot beside text, and a right-hand
 * tooltip would spill over the page — so those go above.
 *
 * `pointer-events-none` either way, so the tooltip can never sit between the
 * cursor and the thing it describes.
 */
function IconTip({ label, children, block, side = 'top' }: {
  label: string; children: React.ReactNode;
  /** Full-width block, for stacked contexts like the collapsed nav rail. */
  block?: boolean;
  side?: 'top' | 'right';
}) {
  const place = side === 'right'
    ? 'left-full top-1/2 ml-2 -translate-y-1/2'
    : 'bottom-full left-1/2 mb-1.5 -translate-x-1/2';
  return (
    <span className={`group/tip relative ${block ? 'flex w-full' : 'inline-flex'}`}>
      {children}
      <span
        role="tooltip"
        className={`pointer-events-none absolute z-50 whitespace-nowrap rounded-md bg-inkblack px-2 py-1 text-[11px] font-medium text-paper opacity-0 shadow-sm transition-opacity duration-100 group-hover/tip:opacity-100 ${place}`}
      >
        {label}
      </span>
    </span>
  );
}

function SidebarContent({
  onNavigate,
  collapsed,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  const initials = (user?.name || user?.email || 'od').slice(0, 2).toUpperCase();
  const roleLevel = `${user?.role}.${user?.level}`;
  const ctx = contextTag(roleLevel);
  const nav = NAV.filter((n) => !n.professionalOnly || !isEntityUser(roleLevel));

  return (
    <div className="flex h-full flex-col">
      {/* brand + context badge */}
      <div className={`pb-2 pt-5 ${collapsed ? 'flex flex-col items-center px-0' : 'px-5'}`}>
        <Link to="/dashboard" onClick={onNavigate} className="inline-block" title="Go to dashboard">
          <img
            src={collapsed ? brand.icon : brand.logo}
            alt="oddits"
            className={collapsed ? 'h-8 w-8' : 'h-11 w-auto'}
          />
        </Link>
        {collapsed ? (
          <span className={`mt-2 size-2 rounded-full ${ctx.dot}`} title={ctx.label} />
        ) : (
          <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${ctx.cls}`}>
            {ctx.label}
          </span>
        )}
      </div>


      {/* nav */}
      <nav className="flex-1 space-y-0.5 px-3 py-1">
        {nav.map((n) => {
          // Team/Activity are tabs under Settings — keep Settings lit there too.
          const active = isActive(n.href) || (n.href === '/admin/settings' && (isActive('/admin/team') || isActive('/admin/audit')));
          const link = (
            <NavLink
              to={n.href}
              onClick={onNavigate}
              className={`flex items-center rounded-xl text-sm font-medium no-underline transition-colors ${
                collapsed ? 'w-full justify-center px-0 py-2' : 'gap-2.5 px-3 py-2'
              } ${
                active
                  ? 'bg-inkblack text-paper'
                  : 'text-inkblack hover:bg-highlighter-80'
              }`}
            >
              <n.icon className={`h-4.5 w-4.5 shrink-0 ${active ? 'text-highlighter' : 'text-inkblack-80'}`} />
              {!collapsed && n.label}
            </NavLink>
          );
          // Collapsed, the icon is the only thing left — it needs the label back
          // on hover or the rail is a guessing game.
          return collapsed
            ? <IconTip key={n.href} label={n.label} block side="right">{link}</IconTip>
            : <span key={n.href} className="block">{link}</span>;
        })}
      </nav>

      {/*
        Account block. Settings and Sign out sit as icons beside the name rather
        than as their own rows: both are account-level, neither is somewhere you
        work, and giving them full nav rows put them at the same weight as GST
        and Books. Collapsed, the avatar carries the same two actions beneath it.
      */}
      <div className="border-t border-ink-100 p-3">
        {collapsed ? (
          <div className="flex flex-col items-center gap-1">
            <span
              className="grid size-9 place-items-center rounded-lg bg-inkblack text-xs font-semibold text-white"
              title={`${user?.name || 'Account'} · ${user?.email ?? ''}`}
            >
              {initials}
            </span>
            {!isEntityUser(roleLevel) && (
              <IconTip label="Settings" side="right">
                <NavLink
                  to="/admin/settings"
                  onClick={onNavigate}
                  aria-label="Settings"
                  className="grid size-8 place-items-center rounded-lg text-inkblack-60 no-underline transition-colors hover:bg-highlighter-80 hover:text-inkblack-80"
                >
                  <Settings className="h-4 w-4" />
                </NavLink>
              </IconTip>
            )}
            <IconTip label="Sign out" side="right">
              <button
                onClick={() => logout()}
                aria-label="Sign out"
                className="grid size-8 place-items-center rounded-lg text-inkblack-60 transition-colors hover:bg-danger-50 hover:text-danger-600"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </IconTip>
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-xl px-2 py-1.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-inkblack text-xs font-semibold text-white">
              {initials}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-inkblack">{user?.name || 'Account'}</p>
              <p className="truncate text-xs text-inkblack-60">{user?.email}</p>
            </div>
            {!isEntityUser(roleLevel) && (
              <IconTip label="Settings">
                <NavLink
                  to="/admin/settings"
                  onClick={onNavigate}
                  aria-label="Settings"
                  className={({ isActive }) =>
                    `grid size-8 shrink-0 place-items-center rounded-lg no-underline transition-colors ${
                      isActive ? 'bg-inkblack text-paper' : 'text-inkblack-80 hover:bg-highlighter-80 hover:text-inkblack'
                    }`
                  }
                >
                  <Settings className="h-4 w-4" />
                </NavLink>
              </IconTip>
            )}
            <IconTip label="Sign out">
              <button
                onClick={() => logout()}
                aria-label="Sign out"
                className="grid size-8 shrink-0 place-items-center rounded-lg text-inkblack-60 transition-colors hover:bg-danger-50 hover:text-danger-600"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </IconTip>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminLayoutRoute() {
  const { user, isLoading } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('odd:sidebar') === 'collapsed',
  );

  // Platform operators (admin.system) have their OWN console at /platform —
  // the CA/professional shell here (entity switcher, professional nav) is the wrong context
  // for them. Bounce them out. Safe against impersonation: logging in as a
  // professional swaps the session to that professional's admin.tenant token, so an
  // impersonating operator is no longer admin.system and stays in this shell.
  if (!isLoading && user?.role === 'admin' && user?.level === 'system') {
    return <Navigate to="/platform" replace />;
  }
  // Entities is where a firm manages its clients. A client's own login has no
  // business there — the sidebar already hides the link, and this closes the
  // typed URL and any stray link to it.
  if (!isLoading && isEntityUser(roleOf(user)) && /^\/admin\/entities(\/|$)/.test(pathname)) {
    return <Navigate to="/dashboard" replace />;
  }
  const toggleCollapse = () =>
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('odd:sidebar', next ? 'collapsed' : 'expanded'); } catch { /* ignore */ }
      return next;
    });

  return (
    <AuthGuard requiredRoles={DEFAULT_ADMIN_ROLES}>
      <CompanyProvider>
      <PeriodProvider>
      <div className="min-h-screen bg-receipt-40 text-inkblack antialiased">
        {/* fixed sidebar (md+) — collapses to an icon rail */}
        <aside
          className={`fixed inset-y-0 left-0 z-30 hidden border-r border-highlighter-80 bg-highlighter transition-[width] duration-200 md:block ${
            collapsed ? 'w-16' : 'w-64'
          }`}
        >
          <SidebarContent collapsed={collapsed} />
        </aside>

        {/* mobile drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-inkblack/40" onClick={() => setMobileOpen(false)} />
            <aside className="absolute inset-y-0 left-0 w-64 border-r border-highlighter-80 bg-highlighter shadow-xl">
              <button
                onClick={() => setMobileOpen(false)}
                className="absolute right-3 top-4 text-inkblack-60 hover:text-inkblack-80"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </aside>
          </div>
        )}

        {/* content */}
        <div className={`transition-[padding] duration-200 ${collapsed ? 'md:pl-16' : 'md:pl-64'}`}>
          {/*
            Workspace header — sticky, on every breakpoint.

            Scope (which client, which financial year) lives here rather than in
            the sidebar because it applies to the CONTENT, not to navigation:
            it stays visible and in the same place while the page below it
            changes, and it survives the sidebar being collapsed.
          */}
          <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b border-receipt bg-paper/90 pl-2 pr-3 backdrop-blur sm:pr-6">
            <button
              onClick={() => setMobileOpen(true)}
              className="grid size-9 place-items-center rounded-xl text-inkblack-60 transition-colors hover:bg-highlighter-80 md:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <button
              onClick={toggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden size-9 items-center justify-center rounded-xl text-inkblack-60 transition-colors hover:bg-highlighter-80 hover:text-inkblack md:flex"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/dashboard" className="inline-block md:hidden" title="Go to dashboard">
              <img src={brand.logo} alt="oddits" className="h-7 w-auto" />
            </Link>

            <div className="ml-auto flex items-center gap-2">
              <CompanySwitcher />
              <FySwitcher />
            </div>
          </header>
          <main className="mx-auto max-w-7xl px-6 py-8">
            <Suspense fallback={<AdminContentFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
      </PeriodProvider>
      </CompanyProvider>
    </AuthGuard>
  );
}
